/**
 * Turns existing cart/menu redux actions into GA4 ecommerce events for places with their own GTM
 * (see clientTracking.js). Nothing happens for any other place: trackEcommerce() is a no-op
 * until initClientGtm() has run.
 *
 *  SET_PRODUCT_PAGE_ID              -> view_item (also on a direct load of a product URL: the
 *                                      view is kept until GTM + consent are ready, then sent once)
 *  ADD_TO_CART (cart changed)       -> add_to_cart / remove_from_cart (only the changed quantity)
 *  SET_CART_MODAL_OPENED_STATE true -> view_cart
 *  SET_ORDER_MODAL_STATE open       -> begin_checkout
 *
 * add_shipping_info / add_payment_info / purchase are sent from products.action.js, where the
 * order is actually submitted.
 *
 * The cart diff works on the ADD_TO_CART payload (the whole cart read back from storage after
 * every change). `restore: true` marks the initial load of a saved cart - that is not an add.
 */
import {
    isGtmMode, trackEcommerce, cartLines, cartLineToItem, cartToItems, itemsValue, onTrackingChange
} from './clientTracking';

const ADD_TO_CART = 'ADD_TO_CART';
const CLEAR_CART = 'CLEAR_CART';
const SET_PRODUCT_PAGE_ID = 'SET_PRODUCT_PAGE_ID';
const REMOVE_PRODUCT_PAGE_ID = 'REMOVE_PRODUCT_PAGE_ID';
const SET_CART_MODAL_OPENED_STATE = 'SET_CART_MODAL_OPENED_STATE';
const SET_ORDER_MODAL_STATE = 'SET_ORDER_MODAL_STATE';

let cartSnapshot = null; // { [product_variant_id]: { qty, line } }
let lastViewedProductId = null;
let orderModalOpen = false;
// Product opened before GTM / consent were ready (direct load of /<place>/<cat>/<product>).
let pendingViewProductId = null;
let trackingSubscribed = false;

const currencyFromState = (state) => {
    try {
        const s = state.settings.settings.default_currency_suffix;
        const suffix = String((s && s.value) || '€').toLowerCase();
        return (suffix.indexOf('лв') !== -1 || suffix.indexOf('bgn') !== -1) ? 'BGN' : 'EUR';
    } catch (e) {
        return 'EUR';
    }
};

const snapshotOf = (toOrder) => {
    const snap = {};
    cartLines(toOrder).forEach(line => {
        const key = String(line.product_variant_id);
        if (!snap[key]) snap[key] = { qty: 0, line: line };
        snap[key].qty += parseInt(line.quantity) || 0;
    });
    return snap;
};

const diffCart = (before, after, currency) => {
    const added = [];
    const removed = [];
    const keys = Object.keys({ ...before, ...after });

    keys.forEach(k => {
        const b = before[k] ? before[k].qty : 0;
        const a = after[k] ? after[k].qty : 0;
        if (a > b) {
            const item = cartLineToItem(after[k].line, a - b);
            if (item) added.push(item);
        } else if (b > a) {
            const item = cartLineToItem(before[k].line, b - a);
            if (item) removed.push(item);
        }
    });

    if (added.length) trackEcommerce('add_to_cart', { currency, value: itemsValue(added), items: added });
    if (removed.length) trackEcommerce('remove_from_cart', { currency, value: itemsValue(removed), items: removed });
};

const findProduct = (state, productId) => {
    const products = state && state.products;
    if (!products) return null;
    const pools = [products.all_products, products.subcat_products];
    for (let i = 0; i < pools.length; i++) {
        const pool = pools[i];
        if (!pool) continue;
        const found = Object.values(pool).find(p => p && p.id == productId);
        if (found) return found;
    }
    return null;
};

/** view_item for a product; true when it was actually sent. */
const sendViewItem = (state, id) => {
    const product = findProduct(state, id);
    if (!product) return false;
    const variant = (product.product_variants || [])[0] || {};
    const item = cartLineToItem({
        product_id: product.id, name: product.name, price: variant.price, size: variant.size, quantity: 1
    });
    if (!item) return false;
    return !!trackEcommerce('view_item', { currency: currencyFromState(state), value: item.price, items: [item] });
};

const currentProductPageId = (state) => (state && state.products && state.products.view_product_page_id) || 0;

const ecommerceMiddleware = storeAPI => next => action => {
    // GTM loads / consent is given after the product page is already open -> send its view then,
    // as long as the customer is still on that product.
    if (!trackingSubscribed) {
        trackingSubscribed = true;
        onTrackingChange(() => {
            try {
                if (!pendingViewProductId || !isGtmMode()) return;
                const state = storeAPI.getState();
                if (String(currentProductPageId(state)) !== String(pendingViewProductId)) {
                    pendingViewProductId = null;
                    return;
                }
                if (sendViewItem(state, pendingViewProductId)) {
                    lastViewedProductId = pendingViewProductId;
                    pendingViewProductId = null;
                }
            } catch (e) { }
        });
    }

    if (!action || typeof action !== 'object' || !action.type) return next(action);

    const before = storeAPI.getState();
    const result = next(action);

    // Keep the cart snapshot current even before GTM mode is known, so the first real
    // add after the settings arrive is not mistaken for the whole saved cart.
    try {
        switch (action.type) {
            case ADD_TO_CART: {
                const prevSnap = cartSnapshot !== null
                    ? cartSnapshot
                    : snapshotOf(before && before.products ? before.products.to_order : null);
                const nextSnap = snapshotOf(action.payload);
                cartSnapshot = nextSnap;
                if (!action.restore && isGtmMode()) {
                    diffCart(prevSnap, nextSnap, currencyFromState(storeAPI.getState()));
                }
                break;
            }
            case CLEAR_CART:
                cartSnapshot = {};
                break;
            case SET_PRODUCT_PAGE_ID: {
                const id = action.payload;
                if (!id) {
                    lastViewedProductId = null;
                    pendingViewProductId = null;
                    break;
                }
                if (String(id) === String(lastViewedProductId)) break;
                if (sendViewItem(storeAPI.getState(), id)) {
                    lastViewedProductId = id;
                    pendingViewProductId = null;
                } else {
                    // Not ready yet (GTM still loading / no consent) - retried from onTrackingChange.
                    pendingViewProductId = id;
                }
                break;
            }
            case REMOVE_PRODUCT_PAGE_ID:
                lastViewedProductId = null;
                pendingViewProductId = null;
                break;
            case SET_CART_MODAL_OPENED_STATE: {
                if (!action.payload || !isGtmMode()) break;
                const state = storeAPI.getState();
                const items = cartToItems(state.products && state.products.to_order);
                if (items.length) trackEcommerce('view_cart', { currency: currencyFromState(state), value: itemsValue(items), items });
                break;
            }
            case SET_ORDER_MODAL_STATE: {
                const open = !!action.payload;
                const wasOpen = orderModalOpen;
                orderModalOpen = open;
                if (!open || wasOpen || !isGtmMode()) break;
                const state = storeAPI.getState();
                const items = cartToItems(state.products && state.products.to_order);
                if (items.length) trackEcommerce('begin_checkout', { currency: currencyFromState(state), value: itemsValue(items), items });
                break;
            }
            default:
                break;
        }
    } catch (e) {
        // Tracking must never break the menu.
    }

    return result;
};

export default ecommerceMiddleware;
